import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Tabs,
  Tab,
  Paper,
  Chip,
  Stack,
  Link,
  InputAdornment,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close,
  GroupAdd,
  CloudUpload,
  Visibility,
  VisibilityOff,
  UploadFile,
  Download,
  ContentCopy,
  CheckCircle,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { userService, BulkCreatedUser } from '../../services/userService';
import { getHashedColorPair } from '../../utils/colorUtils';
import { parseNameList } from '../../utils/nameList';

interface RoleOption {
  _id: string;
  name: string;
}

interface BulkUploadEmployeesDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  roles: RoleOption[];
}

type Mode = 'csv' | 'paste';

// Split a raw string into a de-duplicated, trimmed name list. Accepts either
// newline- or comma-separated input; a header row equal to "Name" is dropped.
// A pasted or uploaded first column may start with its "Name" header.
const parseNames = (raw: string): string[] => {
  const names = parseNameList(raw);
  return names[0]?.toLowerCase() === 'name' ? names.slice(1) : names;
};

const BulkUploadEmployeesDialog: React.FC<BulkUploadEmployeesDialogProps> = ({
  open,
  onClose,
  onSuccess,
  roles,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<Mode>('csv');
  const [roleId, setRoleId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [csvFilename, setCsvFilename] = useState('');
  const [pastedNames, setPastedNames] = useState('');
  const [csvNames, setCsvNames] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkCreatedUser[] | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Names visible in the preview chips + submitted to the server. Derived
  // from whichever input mode the user is currently on so the preview stays
  // consistent with the active tab.
  const activeNames = useMemo(
    () => (mode === 'csv' ? csvNames : parseNames(pastedNames)),
    [mode, csvNames, pastedNames]
  );

  const canSubmit = activeNames.length > 0 && !!roleId && password.length >= 6 && !submitting;

  const handleClose = useCallback(() => {
    if (submitting) return;
    setMode('csv');
    setRoleId('');
    setPassword('');
    setShowPassword(false);
    setCsvFilename('');
    setCsvNames([]);
    setPastedNames('');
    setResult(null);
    onClose();
  }, [submitting, onClose]);

  const handleFile = useCallback((file: File) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      enqueueSnackbar('Please choose a .csv file', { variant: 'warning' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || '');
      // For a multi-column CSV, keep only the first column (Name).
      const firstColumnJoined = raw
        .split(/\r?\n/)
        .map(line => line.split(',')[0])
        .join('\n');
      const names = parseNames(firstColumnJoined);
      setCsvNames(names);
      setCsvFilename(file.name);
      if (names.length === 0) {
        enqueueSnackbar('No names found in the file', { variant: 'warning' });
      }
    };
    reader.onerror = () => enqueueSnackbar('Failed to read file', { variant: 'error' });
    reader.readAsText(file);
  }, [enqueueSnackbar]);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so choosing the same filename twice still fires onChange.
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const removeName = (name: string) => {
    if (mode === 'csv') {
      setCsvNames(prev => prev.filter(n => n !== name));
    } else {
      setPastedNames(prev => parseNames(prev).filter(n => n !== name).join('\n'));
    }
  };

  const handleDownloadSample = async () => {
    try {
      await userService.downloadBulkSampleCSV();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to download sample', { variant: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await userService.bulkCreate({ names: activeNames, roleId, password });
      setResult(res.users);
      enqueueSnackbar(`${res.created} employee(s) created`, { variant: 'success' });
      onSuccess();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Bulk upload failed', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyIds = () => {
    if (!result) return;
    const text = result.map(u => `${u.name}\t${u.userCode || ''}`).join('\n');
    navigator.clipboard.writeText(text);
    enqueueSnackbar('User IDs copied to clipboard', { variant: 'success' });
  };

  const handleDownloadIds = () => {
    if (!result) return;
    const csv = ['Name,User ID', ...result.map(u => `"${u.name}","${u.userCode || ''}"`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'employee_credentials.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle component="div" sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <GroupAdd sx={{ color: theme.palette.primary.main }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Bulk Upload Employees
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Create many employees at once. Each employee gets a system-generated User ID.
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={submitting}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />

      {result ? (
        // ─── Success view ────────────────────────────────────────────────────
        <>
          <DialogContent sx={{ pt: 3 }}>
            <Alert
              icon={<CheckCircle fontSize="inherit" />}
              severity="success"
              sx={{ mb: 2.5, borderRadius: 2 }}
            >
              <Typography variant="body2" fontWeight={600}>
                {result.length} employee{result.length === 1 ? '' : 's'} created successfully.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Distribute the User IDs below. Every employee signs in with their User ID and the default password you set.
              </Typography>
            </Alert>

            <Paper
              elevation={0}
              sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}
            >
              <Box sx={{ px: 2, py: 1.25, bgcolor: alpha(theme.palette.primary.main, 0.04), display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  Employee Credentials
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={handleCopyIds} sx={{ textTransform: 'none' }}>
                    Copy
                  </Button>
                  <Button size="small" startIcon={<Download fontSize="small" />} onClick={handleDownloadIds} sx={{ textTransform: 'none' }}>
                    Download CSV
                  </Button>
                </Stack>
              </Box>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>User ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.map(u => (
                      <TableRow key={u._id}>
                        <TableCell>{u.name}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{u.userCode || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} variant="contained" sx={{ borderRadius: 2 }}>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        // ─── Input view ─────────────────────────────────────────────────────
        <>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
              <Typography variant="body2">
                All employees in this batch will share the same <strong>role</strong> and <strong>default password</strong>.
                User IDs are generated automatically. Employees can change their password after first login.
              </Typography>
            </Alert>

            {/* Shared: role + password */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                    label="Role"
                  >
                    {roles.length === 0 && (
                      <MenuItem value="" disabled>
                        <em>No roles available</em>
                      </MenuItem>
                    )}
                    {roles.map(r => {
                      const config = getHashedColorPair(r.name, theme.palette.mode === 'dark');
                      return (
                        <MenuItem key={r._id} value={r._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: config.color }} />
                            <Typography variant="body2">{r.name}</Typography>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Default Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  helperText={password.length > 0 && password.length < 6 ? 'Minimum 6 characters' : 'Used for every employee in this batch'}
                  error={password.length > 0 && password.length < 6}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Mode tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs
                value={mode}
                onChange={(_, v) => setMode(v)}
                sx={{
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, minHeight: 44 },
                }}
              >
                <Tab value="csv" label="Upload CSV" icon={<UploadFile fontSize="small" />} iconPosition="start" />
                <Tab value="paste" label="Paste Names" icon={<GroupAdd fontSize="small" />} iconPosition="start" />
              </Tabs>
            </Box>

            {/* CSV input */}
            {mode === 'csv' && (
              <Box>
                <Paper
                  elevation={0}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  sx={{
                    cursor: 'pointer',
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 2,
                    border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.divider}`,
                    bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                    },
                  }}
                >
                  <CloudUpload sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" fontWeight={600}>
                    {csvFilename || 'Drop CSV here or click to browse'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    A single <strong>Name</strong> column. Extra columns are ignored.
                  </Typography>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={handleFilePick}
                  />
                </Paper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.25 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={handleDownloadSample}
                    underline="hover"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem' }}
                  >
                    <Download fontSize="small" />
                    Download sample CSV
                  </Link>
                  {csvNames.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {csvNames.length} name{csvNames.length === 1 ? '' : 's'} detected
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Paste input */}
            {mode === 'paste' && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  maxRows={10}
                  value={pastedNames}
                  onChange={(e) => setPastedNames(e.target.value)}
                  placeholder={'Ali Khan, Sara Ahmed, Bilal Hussain\n— or one name per line —'}
                  helperText="Separate names with commas or new lines."
                />
                {activeNames.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {activeNames.length} name{activeNames.length === 1 ? '' : 's'} detected
                  </Typography>
                )}
              </Box>
            )}

            {/* Preview chips */}
            {activeNames.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  maxHeight: 180,
                  overflowY: 'auto',
                }}
              >
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  {activeNames.map(n => {
                    const c = getHashedColorPair(n, theme.palette.mode === 'dark');
                    return (
                      <Chip
                        key={n}
                        label={n}
                        size="small"
                        onDelete={() => removeName(n)}
                        sx={{
                          borderRadius: 1.5,
                          bgcolor: c.bgColor,
                          color: c.color,
                          fontWeight: 500,
                          border: `1px solid ${alpha(c.color, 0.25)}`,
                          '& .MuiChip-deleteIcon': {
                            color: alpha(c.color, 0.6),
                            '&:hover': { color: c.color },
                          },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Paper>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
            >
              {submitting
                ? 'Creating…'
                : `Create ${activeNames.length || ''} Employee${activeNames.length === 1 ? '' : 's'}`.trim()}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default BulkUploadEmployeesDialog;
